import * as React from 'react';
import Select from 'react-select';
import { useToken } from '@chakra-ui/react';

/**
 * Merge caller style overrides onto the defaults below.
 *
 * react-select styles are functions, so an override can't just replace the
 * key — it receives the already-defaulted styles as its base and layers on
 * top. Keys the caller doesn't mention keep the default untouched.
 */
const mergeStyles = (base, overrides) => {
  if (!overrides) return base;

  const merged = { ...base };
  Object.entries(overrides).forEach(([key, override]) => {
    const baseFn = base[key];
    merged[key] = (provided, state) =>
      override(baseFn ? baseFn(provided, state) : provided, state);
  });
  return merged;
};

/**
 * Project-styled react-select.
 *
 * Typed via JSDoc because several defaults below are `undefined`, which
 * TypeScript otherwise infers as the literal `undefined` type — narrowing
 * those props to unusable in .tsx consumers. Keep this list complete: a
 * partial JSDoc becomes the *whole* prop type and breaks every call site.
 *
 * @param {{
 *   height?: number,
 *   borderColor?: string,
 *   indicatorSeparatorColor?: string,
 *   isClearable?: boolean,
 *   isDisabled?: boolean,
 *   isMulti?: boolean,
 *   isSearchable?: boolean,
 *   menuPlacement?: 'auto' | 'bottom' | 'top',
 *   onChange?: (value: any) => void,
 *   placeholder?: string,
 *   options?: any,
 *   value?: any,
 *   width?: number | string | null,
 *   maxWidth?: number | string | null,
 *   inputRef?: any,
 *   onBlur?: (e: any) => void,
 *   styles?: Record<string, (base: any, state: any) => any>,
 *   components?: Record<string, any>,
 * }} props `styles` is merged onto the project defaults rather than replacing
 *   them; `components` passes straight through to react-select for call sites
 *   that need extra chrome inside the control.
 */
const CustomSelect = ({
  height = 48,
  borderColor = '',
  indicatorSeparatorColor = '',
  isClearable = false,
  isDisabled = false,
  isMulti = false,
  isSearchable = false,
  menuPlacement = 'bottom',
  onChange,
  placeholder = '',
  options = [],
  value,
  width = null,
  maxWidth = 340,
  inputRef = undefined,
  onBlur = undefined,
  styles = undefined,
  components = undefined,
}) => {
  const [
    muted100,
    primary50,
    primary100,
    primary800,
    secondary100,
    secondary500,
  ] = useToken('colors', [
    'muted.100',
    'primary.50',
    'primary.100',
    'primary.800',
    'secondary.100',
    'secondary.500',
  ]);

  return (
    <Select
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMulti={isMulti}
      isSearchable={isSearchable}
      menuPlacement={menuPlacement}
      menuPortalTarget={document.body}
      menuShouldScrollIntoView={false}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      onBlur={onBlur}
      components={components}
      styles={mergeStyles({
        menuPortal: (base) => ({
          ...base,
          fontSize: '14px',
          maxWidth,
          zIndex: 9999,
        }),
        container: (base) => ({
          ...base,
          width,
        }),
        control: (base, state) => ({
          ...base,
          fontSize: '14px',
          height,
          maxWidth,
          borderColor,
          backgroundColor: state.isDisabled
            ? muted100
            : base.backgroundColor,
        }),
        indicatorSeparator: (base) => ({
          ...base,
          backgroundColor: indicatorSeparatorColor,
        }),
        valueContainer: (base) => ({
          ...base,
          overflowY: 'auto',
          width: '100%',
          height: '100%',
        }),
      }, styles)}
      theme={(theme) => ({
        ...theme,
        borderRadius: 4,
        colors: {
          ...theme.colors,
          neutral20: secondary500,
          neutral30: secondary100,
          primary: primary800,
          primary25: primary50,
          primary50: primary100,
        },
      })}
      value={value}
      ref={inputRef}
    />
  );
};

export { CustomSelect as Select };
